import React, { useEffect, useRef, useState } from "react";
import "./Greeting.css";
import { Link } from "react-router-dom";
import { greeting, skills, socialMediaLinks } from "../../portfolio";
import { gsap, prefersReducedMotion } from "../../utils/gsap";

// Window-control glyphs for the stack.config title bar
const Glyph = ({ d }) => (
  <svg viewBox="0 0 10 10" width="8" height="8" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
    <path d={d} />
  </svg>
);

const heroLinks = ["Github", "LinkedIn", "Gmail"];

export default function Greeting() {
  const root = useRef(null);
  // stack.config window state: "normal" | "min" | "max" | "closed"
  const [view, setView] = useState("normal");
  const links = socialMediaLinks.filter((s) => heroLinks.includes(s.name));

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const ctx = gsap.context(() => {
      // intro timeline
      gsap
        .timeline({ defaults: { ease: "power3.out" } })
        .from(".eyebrow", { y: 20, opacity: 0, duration: 0.6 })
        .from(".hero-name", { y: 20, opacity: 0, duration: 0.7 }, "-=0.35")
        .from(
          ".hero-title .line",
          { yPercent: 115, duration: 1, stagger: 0.14 },
          "-=0.3"
        )
        .from(".hero-sub", { y: 24, opacity: 0, duration: 0.8 }, "-=0.6")
        .from(
          ".hero-actions .btn",
          { y: 20, opacity: 0, duration: 0.6, stagger: 0.1 },
          "-=0.5"
        )
        .from(
          ".hero-links li",
          { y: 12, opacity: 0, duration: 0.5, stagger: 0.08 },
          "-=0.4"
        )
        .from(
          ".hero-panel-wrap",
          { x: 60, opacity: 0, duration: 1 },
          0.4
        )
        .from(
          ".panel-row",
          { x: 24, opacity: 0, duration: 0.6, stagger: 0.12 },
          0.9
        );

      // scroll-driven exit
      const scrub = {
        trigger: root.current,
        start: "top top",
        end: "bottom top",
        scrub: true,
      };
      gsap.to(".hero-copy", {
        y: -110,
        opacity: 0.1,
        ease: "none",
        scrollTrigger: scrub,
      });
      gsap.to(".hero-panel-wrap", {
        y: -170,
        rotationX: 14,
        rotationY: -10,
        scale: 0.92,
        ease: "none",
        scrollTrigger: scrub,
      });
      gsap.to(".hero-glow", {
        y: 220,
        ease: "none",
        scrollTrigger: scrub,
      });
    }, root);
    return () => ctx.revert();
  }, []);

  return (
    <section className="hero" id="greeting" ref={root}>
      <div className="hero-glow" aria-hidden="true" />
      <div className="hero-inner">
        <div className="hero-copy">
          <span className="eyebrow">
            <i className="eyebrow-dot" /> Full-stack · Mobile · Cloud
          </span>
          <p className="hero-name">
            <span className="hero-name-line" aria-hidden="true" />
            {greeting.title}
          </p>
          <h1 className="hero-title">
            <span className="line-mask">
              <span className="line">Engineering products</span>
            </span>
            <span className="line-mask">
              <span className="line gradient-text">built to scale.</span>
            </span>
          </h1>
          <p className="hero-sub">
            {greeting.subTitle}
          </p>
          <div className="hero-actions">
            <Link to="/projects" className="btn btn-primary">
              View projects
            </Link>
            <a
              className="btn btn-ghost"
              href={greeting.resumeLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download résumé
            </a>
          </div>
          <ul className="hero-links">
            {links.map((s) => (
              <li key={s.name}>
                <a href={s.link} target="_blank" rel="noopener noreferrer">
                  {s.name === "Gmail" ? "Email" : s.name} ↗
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div className="hero-panel-wrap">
          {view === "closed" ? (
            <button
              type="button"
              className="panel-reopen"
              onClick={() => setView("normal")}
            >
              <span aria-hidden="true">▸</span> Reopen stack.config
            </button>
          ) : (
            <div
              className={`hero-panel is-${view}`}
              aria-label="Technology focus"
            >
              <div className="panel-bar">
                <div className="win-controls" role="group" aria-label="Window controls">
                  <button
                    type="button"
                    className="win win-close"
                    aria-label="Close"
                    title="Close"
                    onClick={() => setView("closed")}
                  >
                    <Glyph d="M2 2l6 6M8 2l-6 6" />
                  </button>
                  <button
                    type="button"
                    className="win win-min"
                    aria-label={view === "min" ? "Restore" : "Minimize"}
                    title={view === "min" ? "Restore" : "Minimize"}
                    onClick={() => setView(view === "min" ? "normal" : "min")}
                  >
                    <Glyph d="M2 5h6" />
                  </button>
                  <button
                    type="button"
                    className="win win-max"
                    aria-label={view === "max" ? "Restore size" : "Maximize"}
                    title={view === "max" ? "Restore size" : "Maximize"}
                    onClick={() => setView(view === "max" ? "normal" : "max")}
                  >
                    <Glyph d="M5 2v6M2 5h6" />
                  </button>
                </div>
                <em>stack.config</em>
              </div>
              <div className="panel-collapse">
                <div className="panel-body">
                  {skills.data.map((group, i) => (
                    <div className="panel-row" key={group.title}>
                      <span className="panel-key">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="panel-title">{group.title}</span>
                      <span className="panel-count">
                        {group.softwareSkills.length} tools
                      </span>
                    </div>
                  ))}
                  <div className="panel-status">
                    <i /> Available for new projects
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
